import React, { useState, useEffect, useMemo } from 'react';
import { Trans, t } from '@lingui/macro';
import { useDispatch, useSelector } from 'react-redux';
import { useRouteMatch } from 'react-router-dom';
import { IApplicationStore } from '../../../store/index';
import { useLingui } from "@lingui/react";
import moment from 'moment';
import Loader from "../global/Loader";
import { Link } from "react-router-dom";
import { setDataStore, EDataActions, EStatisticsDateType } from '../../../reducers/data';
import Tabs, { ITab } from '../global/Tabs';
import FadeAnimation from '../global/FadeAnimation';
import FilterReportType from '../global/filters/FilterReportType';
import FilterDateRange from '../global/filters/FilterDateRange';
import FilterDayWeekMonth from '../global/filters/FilterDayWeekMonth';
import getReports from '../../../reducers/api/getReports';
import ReportDaily from '../global/reports/ReportDaily';
import ReportWeekly from '../global/reports/ReportWeekly';
import ReportMonthly from '../global/reports/ReportMonthly';
import ReportYearly from '../global/reports/ReportYearly';
import { IMart, EPermission } from '../../interfaces/types';
import usePermission from '../../hooks/usePermission';
import { getFilterDateRangeTitle } from '../../helpers/index';

export enum EReportsTabs {
    daily = 'daily',
    weekly = 'weekly',
    monthly = 'monthly',
    yearly = 'yearly',
    downloads = 'downloads',
}

interface IReportFilter {
    martId: number | null;
    referenceDate: moment.Moment;
    yearsBack: number;
}

interface IProps {}

const getDateTypeForTab = (tab?: string): EStatisticsDateType => {
    switch (tab) {
        case EReportsTabs.weekly: return EStatisticsDateType.week;
        case EReportsTabs.monthly: return EStatisticsDateType.month;
        case EReportsTabs.yearly: return EStatisticsDateType.year;
        default: return EStatisticsDateType.day;
    }
};

/** Slovak plural: 1 rok, 2-4 roky, 5+ rokov */
const yearsBackLabel = (n: number): string => {
    if (n === 1) return t`rok späť`;
    if (n >= 2 && n <= 4) return t`roky späť`;
    return t`rokov späť`;
};

/** Default reference date for each tab: yesterday / last week / last month / last year */
const getDefaultReferenceDate = (tab?: string): moment.Moment => {
    switch (tab) {
        case EReportsTabs.weekly:
            return moment().subtract(1, 'week');
        case EReportsTabs.monthly:
            return moment().subtract(1, 'month');
        case EReportsTabs.yearly:
            // Default to last year
            return moment().subtract(1, 'year');
        default: // daily
            return moment().subtract(1, 'day');
    }
};

const PageReports = (props: IProps) => {
    const dispatch = useDispatch();
    const lingui = useLingui();
    const match = useRouteMatch<{ tabId?: string }>();

    const disDownloads = usePermission([EPermission.disDownloads]);
    const allowDownloads = usePermission([EPermission.downloads]);

    const selectedMarts = useSelector((store: IApplicationStore) => store.dataStore.selectedMarts);
    const reports = useSelector((store: IApplicationStore) => store.dataStore.reports);
    const filter = useSelector((store: IApplicationStore) => store.dataStore.reportsFilter);
    const loading = useSelector((store: IApplicationStore) => store.loadingStore.loadingReports);

    const currentTab = match.params.tabId;

    // --- Pending (uncommitted) report filter ---
    const [pendingFilter, setPendingFilter] = useState<IReportFilter>({
        martId: null,
        referenceDate: getDefaultReferenceDate(currentTab),
        yearsBack: 2,
    });

    // --- Committed (active) report filter – triggers data fetch ---
    const [committedFilter, setCommittedFilter] = useState<IReportFilter | null>(null);

    // Sync mart selection
    useEffect(() => {
        if (selectedMarts.length > 0) {
            if (pendingFilter.martId === null || !selectedMarts.find(m => m.id === pendingFilter.martId)) {
                setPendingFilter(prev => ({ ...prev, martId: selectedMarts[0].id }));
            }
        } else {
            setPendingFilter(prev => ({ ...prev, martId: null }));
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedMarts]);

    // Reset committed filter and reference date when tab changes
    useEffect(() => {
        setCommittedFilter(null);
        setPendingFilter(prev => ({ ...prev, referenceDate: getDefaultReferenceDate(currentTab) }));
    }, [currentTab]);

    const committedMart: IMart | undefined = useMemo(
        () => committedFilter ? selectedMarts.find(m => m.id === committedFilter.martId) : undefined,
        [selectedMarts, committedFilter]
    );

    const pendingMart: IMart | undefined = useMemo(
        () => selectedMarts.find(m => m.id === pendingFilter.martId),
        [selectedMarts, pendingFilter.martId]
    );

    // Check if pending differs from committed
    const isDirty = committedFilter === null ||
        committedFilter.martId !== pendingFilter.martId ||
        committedFilter.referenceDate.format('YYYY-MM-DD') !== pendingFilter.referenceDate.format('YYYY-MM-DD') ||
        committedFilter.yearsBack !== pendingFilter.yearsBack;

    // Handle date picker selection
    const handleDateSelection = (ranges: { from: string; to: string }[]) => {
        if (ranges.length > 0) {
            const lastRange = ranges[ranges.length - 1];
            setPendingFilter(prev => ({
                ...prev,
                referenceDate: moment(lastRange.from),
            }));
        }
    };

    const handleSubmit = () => {
        setCommittedFilter({ ...pendingFilter });
    };

    // --- Downloads tab ---
    useEffect(() => {
        if (currentTab === EReportsTabs.downloads) {
            dispatch(getReports(
                lingui.i18n.locale,
                selectedMarts.map(m => m.id)
            ));
        }
    }, [dispatch, selectedMarts, lingui.i18n.locale, currentTab]);

    const filteredReports = useMemo(() => {
        let newReports = reports;
        if (filter.type.length > 0) {
            newReports = newReports.filter(r => filter.type.map(tp => tp.id).includes(r.type));
        }
        if (filter.dayFrom) {
            newReports = newReports.filter(r => moment(r.date).isSameOrAfter(filter.dayFrom));
        }
        if (filter.dayTo) {
            newReports = newReports.filter(r => moment(r.date).isSameOrBefore(filter.dayTo));
        }
        return newReports;
    }, [filter, reports]);

    const types = useMemo(() => {
        let newTypes: { id: string; title: string }[] = [];
        reports.forEach(r => {
            if (typeof newTypes.find(tp => tp.id === r.type) === 'undefined') {
                newTypes.push({ id: r.type, title: r.typeTitle });
            }
        });
        return newTypes;
    }, [reports]);

    // --- Tabs ---
    const tabs: ITab[] = [
        { link: '/app/reports', tabId: null, title: t`Denný` },
        { link: '/app/reports/' + EReportsTabs.weekly, tabId: EReportsTabs.weekly, title: t`Týždenný` },
        { link: '/app/reports/' + EReportsTabs.monthly, tabId: EReportsTabs.monthly, title: t`Mesačný` },
        { link: '/app/reports/' + EReportsTabs.yearly, tabId: EReportsTabs.yearly, title: t`Ročný` },
        ...((allowDownloads || disDownloads) ? [{ link: '/app/reports/' + EReportsTabs.downloads, tabId: EReportsTabs.downloads, title: t`Na stiahnutie`, disabled: disDownloads }] : []),
    ];

    const isInteractiveTab =
        typeof currentTab === 'undefined' ||
        currentTab === EReportsTabs.daily ||
        currentTab === EReportsTabs.weekly ||
        currentTab === EReportsTabs.monthly ||
        currentTab === EReportsTabs.yearly;

    const dateType = getDateTypeForTab(currentTab);

    const selectedDateRanges = useMemo(() => {
        const d = pendingFilter.referenceDate;
        switch (dateType) {
            case EStatisticsDateType.day:
                return [{ from: d.format('YYYY-MM-DD'), to: d.format('YYYY-MM-DD') }];
            case EStatisticsDateType.week:
                return [{ from: d.clone().startOf('week').format('YYYY-MM-DD'), to: d.clone().endOf('week').format('YYYY-MM-DD') }];
            case EStatisticsDateType.month:
                return [{ from: d.clone().startOf('month').format('YYYY-MM-DD'), to: d.clone().endOf('month').format('YYYY-MM-DD') }];
            case EStatisticsDateType.year:
                return [{ from: d.clone().startOf('year').format('YYYY-MM-DD'), to: d.clone().endOf('year').format('YYYY-MM-DD') }];
            default:
                return [];
        }
    }, [pendingFilter.referenceDate, dateType]);

    return (
        <>
            <div className="grid small-space align-start pdt-40">
                <h1 className="title_h1 col_default">
                    VISITOR <span className="text_w400"><Trans>reporty</Trans></span>
                </h1>
                <div className="icon_info wh24 col_default">
                    <svg className="sicofill"><use xlinkHref="/assets/layout/icons.svg#s_info" /></svg>
                </div>
            </div>

            <p className="text_basic mgt-20 wmax460">
                <Trans>Vygenerované špecifické denné, týždenné, mesačné a alebo ročné správy tzv. reporty. Tu ich máte vždy poruke.</Trans>
            </p>

            {selectedMarts.length === 0 ? (
                <div className="box mgt-40 mgb-30 pd-40 pdl-25lg pdr-25lg">
                    <div className="message-wrapper">
                        <div className="message-content message-content--warning">
                            <Trans>Musíte vybrať aspoň jedno obchodné centrum.</Trans>
                        </div>
                    </div>
                </div>
            ) : (
                <>
                    <div className="tabs mgt-40">
                        <Tabs tabs={tabs} />
                    </div>

                    <FadeAnimation animKey={currentTab ? currentTab : ''}>
                        {isInteractiveTab && (
                            <>
                                <div className="box mgt-40 mgb-30 pd-40 pdl-25lg pdr-25lg statistics">
                                    <div className="grid flex-nowrap-lg small-space align-start pdt-15 pdb-25">
                                        <div className="col_fill">
                                            <div className="grid small-space">
                                                <div className="element_drop basic to_left small col_default offset">
                                                    <div className="input_select_cont">
                                                        <select
                                                            className="input_select"
                                                            value={pendingFilter.martId ?? ''}
                                                            onChange={(e) => setPendingFilter(prev => ({ ...prev, martId: Number(e.target.value) }))}
                                                        >
                                                            {selectedMarts.map(m => (
                                                                <option key={m.id} value={m.id}>{m.name}</option>
                                                            ))}
                                                        </select>
                                                        <svg className="sico"><use xlinkHref="/assets/layout/icons.svg#s_ar1_d" /></svg>
                                                    </div>
                                                </div>

                                                <FilterDayWeekMonth
                                                    dateType={[dateType]}
                                                    selectedDateType={dateType}
                                                    selectedRanges={selectedDateRanges}
                                                    onSelectedRanges={handleDateSelection}
                                                />

                                                <div className="element_drop basic to_left small col_default offset">
                                                    <div className="input_select_cont">
                                                        <select
                                                            className="input_select"
                                                            value={pendingFilter.yearsBack}
                                                            onChange={(e) => setPendingFilter(prev => ({ ...prev, yearsBack: Number(e.target.value) }))}
                                                        >
                                                            {[1, 2, 3, 4, 5].map(n => (
                                                                <option key={n} value={n}>{n} {yearsBackLabel(n)}</option>
                                                            ))}
                                                        </select>
                                                        <svg className="sico"><use xlinkHref="/assets/layout/icons.svg#s_ar1_d" /></svg>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {isDirty && (
                                            <div className="text_right col_default col1_lgm order-first-lgm self-center offset">
                                                <a
                                                    href="/"
                                                    title=""
                                                    className="btn_action text_nowrap text_w500"
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        handleSubmit();
                                                    }}
                                                >
                                                    <svg className="sico wh16"><use xlinkHref="/assets/layout/icons.svg#s_save" /></svg>
                                                    <span><Trans>Odoslať</Trans></span>
                                                </a>
                                            </div>
                                        )}
                                    </div>

                                    <hr />

                                    <div className="grid small-space flex-nowrap-lg pdt-20">
                                        <div className="col_default col1_lgm text_s16 text_w500 self-center mgt-10"><Trans>Filter</Trans>:</div>
                                        <div className="btn_filter_list col_default col1_lgm">
                                            {pendingMart && (
                                                <a href="/" style={{ pointerEvents: 'none' }} className="btn_filter">
                                                    <span>{pendingMart.name}</span>
                                                </a>
                                            )}
                                            <a href="/" style={{ pointerEvents: 'none' }} className="btn_filter">
                                                <span>
                                                    {getFilterDateRangeTitle(
                                                        selectedDateRanges[0]?.from ?? pendingFilter.referenceDate.format('YYYY-MM-DD'),
                                                        selectedDateRanges[0]?.to ?? pendingFilter.referenceDate.format('YYYY-MM-DD'),
                                                        lingui.i18n,
                                                        dateType
                                                    )}
                                                </span>
                                            </a>
                                            <a href="/" style={{ pointerEvents: 'none' }} className="btn_filter">
                                                <span>{pendingFilter.yearsBack} {yearsBackLabel(pendingFilter.yearsBack)}</span>
                                            </a>
                                        </div>
                                    </div>
                                </div>

                                <FadeAnimation animKey={committedFilter ? 'loaded' : 'empty'}>
                                    {committedFilter && committedMart && (
                                        <>
                                            {(typeof currentTab === 'undefined' || currentTab === EReportsTabs.daily) && (
                                                <ReportDaily mart={committedMart} referenceDate={committedFilter.referenceDate} yearsBack={committedFilter.yearsBack} />
                                            )}
                                            {currentTab === EReportsTabs.weekly && (
                                                <ReportWeekly mart={committedMart} referenceDate={committedFilter.referenceDate} yearsBack={committedFilter.yearsBack} />
                                            )}
                                            {currentTab === EReportsTabs.monthly && (
                                                <ReportMonthly mart={committedMart} referenceDate={committedFilter.referenceDate} yearsBack={committedFilter.yearsBack} />
                                            )}
                                            {currentTab === EReportsTabs.yearly && (
                                                <ReportYearly mart={committedMart} referenceDate={committedFilter.referenceDate} yearsBack={committedFilter.yearsBack} />
                                            )}
                                        </>
                                    )}
                                </FadeAnimation>
                            </>
                        )}

                        {currentTab === EReportsTabs.downloads && allowDownloads && !disDownloads && (
                            <div className="loader-wrapper box mgt-40 mgb-30 pd-40 pdl-25lg pdr-25lg">
                                <Loader value={loading} />
                                <h2 className="title_h3"><Trans>Reporty na stiahnutie</Trans></h2>
                                <div className="grid flex-nowrap-lg small-space align-start pdt-15 pdb-25">
                                    <div className="col_fill">
                                        <div className="grid small-space">
                                            <FilterReportType
                                                title={t`Typ reportu`}
                                                values={types}
                                                selected={filter.type}
                                                setFilterSelect={(val) => {
                                                    dispatch(setDataStore(EDataActions.SET_REPORT_FILTERS, { ...filter, type: val }));
                                                }}
                                            />
                                            <FilterDateRange
                                                title={t`Dátum`}
                                                selected={{ dayFrom: filter.dayFrom, dayTo: filter.dayTo }}
                                                setFilterDateRange={(from, to) => {
                                                    dispatch(setDataStore(EDataActions.SET_REPORT_FILTERS, { ...filter, dayFrom: from, dayTo: to }));
                                                }}
                                            />
                                        </div>
                                    </div>
                                </div>
                                <hr />
                                <div className="grid small-space flex-nowrap-lg pdt-20">
                                    <div className="col_default col1_lgm text_s16 text_w500 self-center mgt-10"><Trans>Filter</Trans>:</div>
                                    <div className="btn_filter_list col_default col1_lgm">
                                        {filter.type.map((ft, i) => (
                                            <a key={i} href="/" className="btn_filter" onClick={(e) => { e.preventDefault(); e.stopPropagation(); dispatch(setDataStore(EDataActions.SET_REPORT_FILTERS, { ...filter, type: filter.type.filter(f => f.id !== ft.id) })); }}>
                                                <span>{ft.title}</span>
                                                <svg className="sico"><use xlinkHref="/assets/layout/icons.svg#s_cross" /></svg>
                                            </a>
                                        ))}
                                        {(typeof filter.dayFrom !== 'undefined' || typeof filter.dayTo !== 'undefined') ? (
                                            <a href="/" className="btn_filter" onClick={(e) => { e.preventDefault(); e.stopPropagation(); dispatch(setDataStore(EDataActions.SET_REPORT_FILTERS, { ...filter, dayFrom: undefined, dayTo: undefined })); }}>
                                                {(typeof filter.dayFrom !== 'undefined' && typeof filter.dayTo !== 'undefined') ? (
                                                    <span>{moment(filter.dayFrom).format('DD.MM.YYYY')} – {moment(filter.dayTo).format('DD.MM.YYYY')}</span>
                                                ) : (
                                                    <span>{filter.dayFrom ? moment(filter.dayFrom).format('DD.MM.YYYY') : moment(filter.dayTo).format('DD.MM.YYYY')}</span>
                                                )}
                                                <svg className="sico"><use xlinkHref="/assets/layout/icons.svg#s_cross" /></svg>
                                            </a>
                                        ) : null}
                                    </div>
                                </div>
                                <div className="pdt-90 item_report_list">
                                    {filteredReports.length === 0 ? (
                                        <div className="message-wrapper">
                                            <div className="message-content message-content--warning">
                                                <Trans>Zadaným filtrom nevyhovujú žiadne reporty.</Trans>
                                            </div>
                                        </div>
                                    ) : null}
                                    {filteredReports.map((r, i) => {
                                        let mart = selectedMarts.find(m => m.id === r.mart);
                                        if (typeof mart === 'undefined') return null;
                                        return (
                                            <div className="item_report" key={i}>
                                                <a href={r.link} target="_blank" rel="noreferrer" title={r.name} className="item_report_title">{moment(r.date).format('DD.MM.YYYY')} - {r.name}</a>
                                                <Link to={`/app/map/${r.mart}`} title={mart.name} className="item_report_type">{mart.name}</Link>
                                                <a href={r.link} target="_blank" rel="noreferrer" title={r.name} className="item_report_dl btn_action text_w700">
                                                    <span><Trans>Stiahnuť</Trans></span>
                                                    <svg className="sico wh16"><use xlinkHref="/assets/layout/icons.svg#s_dl" /></svg>
                                                </a>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </FadeAnimation>
                </>
            )}
        </>
    );
};

export default PageReports;
